import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { FormControl } from '@angular/forms';
import { NbToastrService } from '@nebular/theme';
import { Subject, forkJoin, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs/operators';
import {
  LinkableCourse,
  PrerequisiteLink,
  PrerequisiteService,
} from '../../services/prerequisite.service';

export interface CourseOption {
  id: string;
  name: string;
  /** Hat der Kurs eine verantwortliche Person? Ohne sie kann niemand
   *  zustimmen, und das Backend weist die Anfrage ab. Die Auswahl zeigt
   *  solche Kurse deshalb gesperrt an, statt sie erst nach dem Klick
   *  scheitern zu lassen — oder sie stillschweigend wegzulassen, was die
   *  Frage "wo ist mein Kurs" aufwirft. */
  hasResponsible?: boolean;
}

/**
 * Verknüpfte Kursinhalte: welche anderen Kurse der Agent dieses Kurses
 * lesen darf. Deckt Ausschreibungspunkt 3.2 ab — die Oberfläche spricht
 * aber bewusst NICHT deren Sprache („Grundlagenmodul"): gewollt ist,
 * Kursinhalte zu verknüpfen, damit ein Agent auf mehrere Kurse zugreift.
 * Die Bezeichner im Backend (course_prerequisite_links, /prerequisites,
 * source/target) bleiben davon unberührt.
 *
 * Drei Zugriffsstufen, und diese Karte verwaltet nur die dritte: die
 * Curriculum-Freigabe erlaubt einem Agenten, einen Nachbarkurs zu NENNEN,
 * eine bestätigte Verknüpfung erlaubt ihm, dessen Material zu LESEN.
 *
 * Der Status ist bewusst keine Ampel: „jederzeit nachvollziehbar" heißt, dass
 * sichtbar sein muss, wer zugestimmt hat, wer noch fehlt und — wenn sie aus ist
 * — warum. Deshalb eine Zeile je erforderlicher Person.
 */
@Component({
  selector: 'app-course-prerequisites',
  templateUrl: './course-prerequisites.component.html',
  styleUrls: ['./course-prerequisites.component.scss'],
})
export class CoursePrerequisitesComponent implements OnChanges, OnDestroy, OnInit {
  /** Der Kurs, in dem der Agent sitzt. */
  @Input() courseId!: string;
  @Input() courseName = '';
  /** Kandidaten für die Auswahl — Kurse desselben Mandanten. */
  @Input() courses: CourseOption[] = [];
  /** Darf der aktuelle Nutzer hier beantragen? Kommt aus `may_edit`. */
  @Input() canRequest = false;

  links: PrerequisiteLink[] = [];
  loading = false;
  busy = false;

  /** Mehrere Ziele auf einmal: ein Kurs setzt in der Praxis mehrere andere
   *  voraus, und jede Verknüpfung einzeln zu beantragen hieß Dialog auf,
   *  wählen, abschicken, warten — pro Kurs. */
  picked: LinkableCourse[] = [];

  /** Die Suche nach Kursen. Ein Textfeld, keine Liste: die aufrufenden Seiten
   *  haben nur EINE Seite ihrer Tabelle geladen, und eine Auswahl daraus bot
   *  genau diese 50 Kurse an — der Kurs auf Seite vier war unerreichbar.
   *  Gesucht wird deshalb serverseitig über alle Kurse des Mandanten. */
  searchControl = new FormControl('');
  results: LinkableCourse[] = [];
  searching = false;
  /** Treffer insgesamt — `results` ist eine Seite davon. */
  totalMatches = 0;

  /** Die Liste hängt im CDK-Overlay, nicht in der Karte: `nb-card-body`
   *  scrollt, eine absolut gesetzte Liste wäre dort abgeschnitten. Gleiches
   *  Vorgehen wie beim Zeilenmenü von `app-table`. */
  panelOpen = false;
  panelWidth = 0;
  readonly panelPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  @ViewChild('searchField', { read: ElementRef }) searchField?: ElementRef<HTMLElement>;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  private readonly pageSize = 25;
  /** Läuft gerade ein Nachladen? Verhindert, dass jedes Scroll-Ereignis eine
   *  weitere Seite anfordert. */
  loadingMore = false;
  /** Welche Anfrage gilt. Eine spät eintreffende Antwort auf einen alten
   *  Suchbegriff darf die Liste nicht überschreiben. */
  private requestToken = 0;
  private destroy$ = new Subject<void>();

  /** Welche Einträge ausgeklappt sind. Standard ist zu: die Zeile beantwortet
   *  die häufige Frage (welcher Kurs, welcher Stand), die Details braucht man
   *  erst beim Eingreifen. Offen bleibt, was auf MICH wartet. */
  private expanded = new Set<string>();

  isExpanded(l: PrerequisiteLink): boolean {
    return this.expanded.has(l.id) || l.my_state === 'pending';
  }

  toggleExpanded(l: PrerequisiteLink): void {
    if (this.expanded.has(l.id)) this.expanded.delete(l.id);
    else this.expanded.add(l.id);
  }

  constructor(
    private service: PrerequisiteService,
    private toastr: NbToastrService
  ) {}

  ngOnInit(): void {
    // Getippt wird ins Feld, gesucht wird auf dem Server — aber erst, wenn
    // jemand kurz innehält. `distinctUntilChanged` verhindert die zweite
    // Anfrage, wenn ein Zeichen getippt und gleich wieder gelöscht wurde.
    this.searchControl.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.panelOpen = true;
        this.reloadFirstPage();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['courseId'] && this.courseId) {
      this.load();
      // Ohne Tippen schon etwas zu sehen: wer nur zwei Kurse hat, soll nicht
      // raten müssen, wie der zweite heißt.
      this.reloadFirstPage();
    }
  }

  private get term(): string {
    return (this.searchControl.value || '').trim();
  }

  /** Von vorn: neuer Suchbegriff, oder die Karte wurde gerade geöffnet. */
  private reloadFirstPage(): void {
    if (!this.courseId) return;
    const token = ++this.requestToken;
    this.searching = true;
    this.service.searchCourses(this.term, this.courseId, this.pageSize, 0).subscribe({
      next: (res) => {
        if (token !== this.requestToken) return;
        this.searching = false;
        this.results = res.data || [];
        this.totalMatches = res.total || 0;
      },
      error: () => {
        if (token !== this.requestToken) return;
        this.searching = false;
        this.results = [];
        this.totalMatches = 0;
      },
    });
  }

  /** Gibt es noch ungeladene Treffer? */
  get hasMore(): boolean {
    return this.results.length < this.totalMatches;
  }

  /** Die nächste Seite anhängen. Ausgelöst vom Scrollen der Liste — so sieht
   *  man nach und nach alle Kurse, ohne dass die erste Anzeige auf hunderte
   *  Zeilen warten muss. */
  loadMore(): void {
    if (this.loadingMore || this.searching || !this.hasMore || !this.courseId) return;
    const token = this.requestToken;
    const offset = this.results.length;
    this.loadingMore = true;
    this.service
      .searchCourses(this.term, this.courseId, this.pageSize, offset)
      .subscribe({
        next: (res) => {
          this.loadingMore = false;
          // Antwort auf eine überholte Suche, oder die Liste hat sich
          // zwischenzeitlich verschoben: dann gehört sie nicht ans Ende.
          if (token !== this.requestToken || this.results.length !== offset) return;
          this.results = [...this.results, ...(res.data || [])];
          this.totalMatches = res.total ?? this.totalMatches;
        },
        error: () => {
          this.loadingMore = false;
        },
      });
  }

  onPanelScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 64) this.loadMore();
  }

  openPanel(): void {
    this.panelWidth = this.searchField?.nativeElement.offsetWidth || 0;
    this.panelOpen = true;
    if (!this.results.length && !this.searching) this.reloadFirstPage();
  }

  closePanel(): void {
    this.panelOpen = false;
  }

  load(): void {
    if (!this.courseId) return;
    this.loading = true;
    this.service.list(this.courseId).subscribe({
      next: (l) => {
        this.links = l;
        this.loading = false;
      },
      error: () => {
        this.links = [];
        this.loading = false;
      },
    });
  }

  /** Was die Liste zeigt: ohne die bereits verknüpften. Gewählte bleiben
   *  drin und tragen ein Häkchen — sonst verschwände eine Zeile beim Klick
   *  und ließe sich nicht mehr abwählen. */
  get visibleResults(): LinkableCourse[] {
    const taken = new Set(
      this.links
        .filter((l) => l.target_course_id === this.courseId)
        .filter((l) => l.status === 'active' || l.status === 'pending')
        .map((l) => l.source_course_id)
    );
    return this.results.filter((c) => !taken.has(c.id));
  }

  /** Keiner der geladenen Kurse ist wählbar. Dann liegt es nicht an der Suche,
   *  sondern daran, dass für diese Kurse niemand eingetragen ist — und das
   *  gehört einmal gesagt, nicht vierzigmal als graue Zeile. */
  get noneLinkable(): boolean {
    const visible = this.visibleResults;
    return !this.searching && visible.length > 0 && visible.every((c) => !this.isPickable(c));
  }

  /** Ohne das baut Angular beim Nachladen die ganze Liste neu auf — und ein
   *  Klick, der zwischen mousedown und mouseup den Knopf verliert, kommt nie
   *  an (CLAUDE.md Regel 18, gleiche Ursache). */
  trackCourse(_index: number, c: LinkableCourse): string {
    return c.id;
  }

  isSelected(c: LinkableCourse): boolean {
    return this.picked.some((p) => p.id === c.id);
  }

  /** Kurse ohne Verantwortliche kann man nicht verknüpfen. */
  isPickable(c: LinkableCourse): boolean {
    return c.has_responsible !== false;
  }

  /** Woher der Kurs stammt. Zwei Kurse heißen schon mal gleich — ohne die
   *  Herkunft steht man vor zwei identischen Zeilen. */
  sourceLabel(c: LinkableCourse): string {
    const raw = (c.source_type || '').toLowerCase();
    if (raw.includes('ilias')) return 'ILIAS';
    if (raw.includes('moodle')) return 'Moodle';
    return '';
  }

  /** Mehrfachauswahl: der Klick schaltet um, die Liste bleibt offen. */
  toggle(c: LinkableCourse): void {
    if (!c || !this.isPickable(c)) return;
    this.picked = this.isSelected(c)
      ? this.picked.filter((p) => p.id !== c.id)
      : [...this.picked, c];
    this.searchInput?.nativeElement.focus();
  }

  unpick(c: LinkableCourse): void {
    this.picked = this.picked.filter((p) => p.id !== c.id);
  }

  courseName_(id: string): string {
    // Zuerst der Name aus der Verknüpfung selbst: die aufrufende Seite kennt
    // nur die Kurse ihrer aktuellen Tabellenseite, die Antwort des Backends
    // trägt den Namen immer mit.
    const fromLink = this.links.find(
      (l) =>
        (l.source_course_id === id && l.source_course_name) ||
        (l.target_course_id === id && l.target_course_name)
    );
    const name =
      fromLink &&
      (fromLink.source_course_id === id
        ? fromLink.source_course_name
        : fromLink.target_course_name);
    if (name) return name;
    return (this.courses || []).find((c) => c.id === id)?.name || id.slice(0, 8);
  }

  /** Verknüpfungen, in denen DIESER Kurs liest. */
  get incoming(): PrerequisiteLink[] {
    return this.links.filter((l) => l.target_course_id === this.courseId);
  }

  /** Verknüpfungen, in denen ein anderer Kurs DIESEN liest. */
  get outgoing(): PrerequisiteLink[] {
    return this.links.filter((l) => l.source_course_id === this.courseId);
  }

  statusLabel(l: PrerequisiteLink): string {
    switch (l.status) {
      case 'active':
        return 'Freigegeben';
      case 'pending':
        // Eine genuegt — die Zahl der offenen Personen waere irrefuehrend.
        return 'Wartet auf Bestätigung';
      case 'rejected':
        return 'Abgelehnt';
      case 'revoked':
        return 'Widerrufen';
      default:
        return l.status;
    }
  }

  /** Ein Satz, der erklärt, was gerade gilt — nicht nur, dass etwas gilt. */
  statusHint(l: PrerequisiteLink): string {
    if (l.status === 'active') {
      return 'Der Agent dieses Kurses darf die Inhalte des verknüpften Kurses lesen und zitieren.';
    }
    if (l.status === 'pending') {
      return 'Bis eine verantwortliche Person des verknüpften Kurses zustimmt, bleibt der Zugriff strikt auf diesen Kurs beschränkt.';
    }
    const reason = l.closed_reason ? ` (${l.closed_reason})` : '';
    return `Kein Inhaltszugriff${reason}. Der Agent darf den Kurs weiterhin nennen, wenn das Curriculum ihn freigibt.`;
  }

  approvalLabel(a: PrerequisiteLink['approvals'][number]): string {
    const side = a.side === 'target' ? 'dieser Kurs' : 'verknüpfter Kurs';
    const state =
      a.state === 'granted'
        ? 'bestätigt'
        : a.state === 'rejected'
        ? 'abgelehnt'
        : 'ausstehend';
    return a.user_name ? `${side} — ${state} · ${a.user_name}` : `${side} — ${state}`;
  }

  get requestLabel(): string {
    const n = this.picked.length;
    return n > 1 ? `${n} Verknüpfungen beantragen` : 'Verknüpfung beantragen';
  }

  request(): void {
    const ids = this.picked.map((c) => c.id);
    if (!ids.length || this.busy) return;
    this.busy = true;
    // Nacheinander, nicht gebündelt: jede Verknüpfung hat ihr eigenes
    // Ergebnis — eine ist sofort aktiv (man ist dort selbst verantwortlich),
    // die nächste wartet auf Zustimmung, eine dritte scheitert, weil der
    // Zielkurs niemanden hinterlegt hat. Ein einzelner Fehlschlag darf die
    // übrigen nicht mitreißen, deshalb `forkJoin` mit abgefangenem Fehler
    // statt einer Kette, die beim ersten Problem abbricht.
    forkJoin(
      ids.map((id) =>
        this.service.request(this.courseId, id).pipe(
          map((l) => ({ ok: true as const, link: l, id })),
          catchError((e) =>
            of({
              ok: false as const,
              id,
              detail: (e?.error?.detail as string) || 'Anfrage fehlgeschlagen',
            })
          )
        )
      )
    ).subscribe((results) => {
      this.busy = false;
      const failed = results.filter((r) => !r.ok) as {
        ok: false;
        id: string;
        detail: string;
      }[];
      const ok = results.filter((r) => r.ok) as {
        ok: true;
        link: PrerequisiteLink;
        id: string;
      }[];
      // Nur die erledigten aus der Auswahl nehmen — was gescheitert ist,
      // bleibt stehen, damit der Grund sichtbar bleibt und ein zweiter
      // Versuch nicht neu zusammengeklickt werden muss.
      const stillFailing = new Set(failed.map((f) => f.id));
      this.picked = this.picked.filter((c) => stillFailing.has(c.id));

      if (ok.length) {
        const active = ok.filter((r) => r.link.status === 'active').length;
        const notified = ok.reduce(
          (sum, r) => sum + (r.link.notified ?? r.link.pending ?? 0),
          0
        );
        this.toastr.success(
          ok.length === 1
            ? ok[0].link.status === 'active'
              ? 'Verknüpfung ist aktiv.'
              : `Angefragt — ${notified} Person${
                  notified === 1 ? '' : 'en'
                } wurden benachrichtigt.`
            : `${ok.length} Verknüpfungen beantragt` +
              (active ? `, ${active} sofort aktiv` : '') +
              (notified ? ` · ${notified} benachrichtigt` : ''),
          'Kursverknüpfung'
        );
      }
      if (failed.length) {
        // Bei einem einzelnen Fehlschlag den Satz des Backends zeigen (er
        // nennt den Grund); bei mehreren die Namen, sonst steht da nur eine
        // Zahl ohne Anhaltspunkt.
        const names = failed
          .map((f) => this.picked.find((o) => o.id === f.id)?.name || '?')
          .join(', ');
        this.toastr.danger(
          failed.length === 1 ? failed[0].detail : `Nicht beantragt: ${names}`,
          'Kursverknüpfung'
        );
      }
      if (ok.length) this.load();
    });
  }

  /** Managerinnen und Admins duerfen fuer alle entscheiden. Getrennter Knopf,
   *  damit ein Admin, der selbst verantwortlich ist, nicht versehentlich auch
   *  die Antwort der anderen schliesst. */
  canOverride(l: PrerequisiteLink): boolean {
    return !!l.can_override && l.status === 'pending';
  }

  decide(l: PrerequisiteLink, grant: boolean, override = false): void {
    if (this.busy) return;
    this.busy = true;
    this.service.decide(l.id, grant, undefined, override).subscribe({
      next: (r) => {
        this.busy = false;
        this.toastr.success(
          r.status === 'active'
            ? 'Bestätigt — die Verknüpfung ist jetzt aktiv.'
            : grant
            ? 'Bestätigt. Es fehlen noch weitere Zustimmungen.'
            : 'Abgelehnt. Die Verknüpfung bleibt geschlossen.',
          'Kursverknüpfung'
        );
        this.load();
      },
      error: (e) => {
        this.busy = false;
        this.toastr.danger(e?.error?.detail || 'Nicht möglich', 'Kursverknüpfung');
      },
    });
  }

  revoke(l: PrerequisiteLink): void {
    if (this.busy) return;
    this.busy = true;
    this.service.revoke(l.id).subscribe({
      next: () => {
        this.busy = false;
        this.toastr.success(
          'Zustimmung widerrufen — der Inhaltszugriff endet sofort.',
          'Kursverknüpfung'
        );
        this.load();
      },
      error: (e) => {
        this.busy = false;
        this.toastr.danger(e?.error?.detail || 'Nicht möglich', 'Kursverknüpfung');
      },
    });
  }
}
